import { useCallback, useEffect, useRef, useState } from "react";
import {
  FindAllTicketsQueryFilters,
  FindAllTicketsQueryFiltersSortByEnum,
  FindAllTicketsQueryFiltersSortOrderEnum,
  FindAllTicketsQueryFiltersTicketStatusEnum,
  FindAllTicketsQueryFiltersTicketTypeEnum,
  TicketFlatFilterDto,
} from "../../../api-client";
import { useTicketFiltersFlat } from "../../../stores/Ticketing";

export interface TicketFilterState {
  type: FindAllTicketsQueryFiltersTicketTypeEnum[];
  status: FindAllTicketsQueryFiltersTicketStatusEnum[];
  fromDate: Date | null;
  toDate: Date | null;
  sortBy: FindAllTicketsQueryFiltersSortByEnum;
  sortOrder: FindAllTicketsQueryFiltersSortOrderEnum;
}

interface UseTicketFiltersProps {
  baseFilter: FindAllTicketsQueryFilters;
  onFiltersChange: (filters: TicketFilterState) => void;
}

export const useTicketFiltersFlateState = ({
  onFiltersChange,
  baseFilter,
}: UseTicketFiltersProps) => {
  const [filterState, setFilterState] = useState<TicketFilterState>({
    type: [],
    status: [],
    fromDate: null,
    toDate: null,
    sortBy: baseFilter.sortBy || FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
    sortOrder: baseFilter.sortOrder || FindAllTicketsQueryFiltersSortOrderEnum.Asc,
  });

  const [showFilters, setShowFilters] = useState(false);
  const { data: flatFilters } = useTicketFiltersFlat();
  const [translatedFlatTicketFilters, setFlatTranslatedTicketFilters] = useState<
    TicketFlatFilterDto | undefined
  >(flatFilters);
  // Generate filters based on current state

  const prevFilterState = useRef(filterState);
  // Update filters when state changes
  useEffect(() => {
    if (prevFilterState.current !== filterState) {
      onFiltersChange(filterState);
      // update the ref after running
      prevFilterState.current = filterState;
    }
    // onFiltersChange(filterState);
  }, [filterState]);

  useEffect(() => {
    if (flatFilters) {
      setFlatTranslatedTicketFilters(flatFilters);
    }
  }, [flatFilters]);

  // Filter update functions
  const updateType = useCallback((type: FindAllTicketsQueryFiltersTicketTypeEnum[]) => {
    setFilterState((prev) => ({ ...prev, type }));
  }, []);

  const updateStatus = useCallback((status: FindAllTicketsQueryFiltersTicketStatusEnum[]) => {
    setFilterState((prev) => ({ ...prev, status }));
  }, []);

  const updateFromDate = useCallback((fromDate: Date | null) => {
    setFilterState((prev) => ({ ...prev, fromDate }));
  }, []);

  const updateToDate = useCallback((toDate: Date | null) => {
    setFilterState((prev) => ({ ...prev, toDate }));
  }, []);

  const updateSort = useCallback((sortBy: FindAllTicketsQueryFiltersSortByEnum) => {
    setFilterState((prev) => ({
      ...prev,
      sortBy,
      sortOrder:
        prev.sortBy === sortBy
          ? prev.sortOrder === FindAllTicketsQueryFiltersSortOrderEnum.Asc
            ? FindAllTicketsQueryFiltersSortOrderEnum.Desc
            : FindAllTicketsQueryFiltersSortOrderEnum.Asc
          : FindAllTicketsQueryFiltersSortOrderEnum.Desc,
    }));
  }, []);

  // Set the sort explicitly without the asc/desc toggle behaviour of updateSort.
  // Used by on/off style controls (e.g. the "sort by clinic" switch).
  const setSort = useCallback(
    (
      sortBy: FindAllTicketsQueryFiltersSortByEnum,
      sortOrder: FindAllTicketsQueryFiltersSortOrderEnum
    ) => {
      setFilterState((prev) => ({ ...prev, sortBy, sortOrder }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilterState({
      type: [],
      status: [],
      fromDate: null,
      toDate: null,
      sortBy: baseFilter.sortBy || FindAllTicketsQueryFiltersSortByEnum.CreatedAt,
      sortOrder: baseFilter.sortOrder || FindAllTicketsQueryFiltersSortOrderEnum.Asc,
    });
  }, []);

  const toggleFilters = useCallback(() => {
    setShowFilters((prev) => !prev);
  }, []);

  return {
    filterState,
    showFilters,
    updateType,
    updateStatus,
    updateFromDate,
    updateToDate,
    updateSort,
    setSort,
    resetFilters,
    toggleFilters,
    setShowFilters,
    translatedFlatTicketFilters,
  };
};
